import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  IncidentWorkItemParticipant,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import {
  ParticipantRoleSchema,
  ParticipantStatusSchema,
} from "@thalamos/vision-common";

import type { apiHooks } from "../../api/index.js";
import api from "../../api/index.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";

export function useIncidentActions(
  refetchWorkItems: ReturnType<
    typeof apiHooks.incidentWorkItem.getMyActiveIncidentWorkItems
  >[1],
) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const notifications = useToastNotifications();

  const isPendingTransfer = (participant?: IncidentWorkItemParticipant) =>
    participant?.status === ParticipantStatusSchema.enum.pendingTransfer &&
    participant.role === ParticipantRoleSchema.enum.pendingOwner;

  const canCancelTransfer = (participant?: IncidentWorkItemParticipant) =>
    participant?.status === ParticipantStatusSchema.enum.requestedTransfer &&
    participant.role === ParticipantRoleSchema.enum.owner;

  const handleViewIncident = async (
    incident: IncidentWorkItemWithContext,
    matchingParticipant?: IncidentWorkItemParticipant,
  ) => {
    if (isPendingTransfer(matchingParticipant)) {
      await api.incidentWorkItem
        .resolveTransfer({
          incidentWorkItemId: incident.incidentWorkItem.id,
          isAccepted: true,
        })
        .then(async (res) => {
          if (res.status === 204) {
            notifications.show(t("incidentWorkItem.notificationAcceptedShow"), {
              severity: "success",
            });

            await refetchWorkItems();
            navigate(
              createRoute.incidentProgressPage(incident.incidentWorkItem.id),
            );
          } else {
            notifications.show(
              t("incidentWorkItem.notificationAcceptedError"),
              { severity: "error" },
            );
          }
        });
    } else {
      navigate(createRoute.incidentProgressPage(incident.incidentWorkItem.id));
    }
  };

  const handleSecondaryAction = async (
    incident: IncidentWorkItemWithContext,
    matchingParticipant?: IncidentWorkItemParticipant,
  ) => {
    if (canCancelTransfer(matchingParticipant)) {
      await api.incidentWorkItem
        .revokeTransfer({ incidentWorkItemId: incident.incidentWorkItem.id })
        .then(async (res) => {
          if (res.status === 204) {
            notifications.show(
              t("incidentWorkItem.notificationCancelledShow"),
              { severity: "success" },
            );

            await refetchWorkItems();
          } else {
            notifications.show(
              t("incidentWorkItem.notificationCancelledError"),
              { severity: "error" },
            );
          }
        });
    } else if (isPendingTransfer(matchingParticipant)) {
      await api.incidentWorkItem
        .resolveTransfer({
          incidentWorkItemId: incident.incidentWorkItem.id,
          isAccepted: false,
        })
        .then(async (res) => {
          if (res.status === 204) {
            notifications.show(t("incidentWorkItem.notificationRejectedShow"), {
              severity: "success",
            });

            await refetchWorkItems();
          } else {
            notifications.show(
              t("incidentWorkItem.notificationRejectedError"),
              { severity: "error" },
            );
          }
        });
    }
  };

  return {
    isPendingTransfer,
    canCancelTransfer,
    handleViewIncident,
    handleSecondaryAction,
  };
}
